import { useCallback, useContext, useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { AdvancedMarkerContext, useMap } from '@vis.gl/react-google-maps'
import { X } from 'lucide-react'
import { Button, cn, daysUntil, formatDate } from '@asad/crane-ui'

import { alertPresentationOf, alertStatusOf, inspectionToneClass, workingPresentationOf } from '../config/statusConfig'
import { DeviceStatusType, MapDevice } from '../types'

const MAP_EDGE_INSET = 12
const TAIL_INSET = 14

type BlockLayout = {
  below: boolean
  shiftX: number
  tailX?: number
  maxWidth?: number
}

const COMPACT_LAYOUT: BlockLayout = { below: false, shiftX: 0 }

const STATE_CHIP_CLASS = 'flex size-6 items-center justify-center rounded-crane-md'

const sameLayout = (a: BlockLayout, b: BlockLayout) =>
  a.below === b.below && a.shiftX === b.shiftX && a.tailX === b.tailX && a.maxWidth === b.maxWidth

interface InfoRowProps {
  label: string
  children: ReactNode
  valueClassName?: string
}

const InfoRow = ({ label, children, valueClassName }: InfoRowProps) => (
  <div className='flex items-baseline gap-2 leading-4'>
    <span className='w-crane-info-label shrink-0 text-crane-xs text-crane-sidebar-foreground'>{label}</span>
    <span className={cn('min-w-0 flex-1 break-words text-xs', valueClassName)}>{children}</span>
  </div>
)

interface BlockTailProps {
  x?: number
  up?: boolean
}

// Klasy bez `cn()`: tailwind-merge nie zna skali `crane-` i wycina szerokość obramowania
// na rzecz koloru tej samej krawędzi (`border-t-crane-tail-tip` kontra `border-t-crane-primary-foreground`).
const BlockTail = ({ x, up = false }: BlockTailProps) => {
  const anchor = x === undefined ? 'left-1/2' : ''
  const outline = up
    ? 'bottom-0 border-b-crane-tail-tip border-b-crane-primary-foreground'
    : 'top-0 border-t-crane-tail-tip border-t-crane-primary-foreground'
  const fill = up
    ? 'bottom-0 border-b-crane-tail-tip-sm border-b-crane-sidebar'
    : 'top-0 border-t-crane-tail-tip-sm border-t-crane-sidebar'
  const style = x === undefined ? undefined : { left: x }
  return (
    <span aria-hidden className='relative block h-crane-marker-tail w-full'>
      <span
        className={`absolute h-0 w-0 -translate-x-1/2 border-solid border-x-crane-tail-side border-x-transparent ${anchor} ${outline}`}
        style={style}
      />
      <span
        className={`absolute h-0 w-0 -translate-x-1/2 border-solid border-x-crane-tail-side-sm border-x-transparent ${anchor} ${fill}`}
        style={style}
      />
    </span>
  )
}

export interface DeviceMarkerProps {
  device: MapDevice
  deviceStatus?: DeviceStatusType
  active?: boolean
  expanded?: boolean
  locationLabel?: string
  onClick?: (deviceId: string) => void
  onClose?: () => void
  onDetails?: (deviceId: string) => void
}

export const DeviceMarker = ({
  device,
  deviceStatus,
  active = false,
  expanded = false,
  locationLabel,
  onClick,
  onClose,
  onDetails,
}: DeviceMarkerProps) => {
  const { t, i18n } = useTranslation()
  const map = useMap()
  const marker = useContext(AdvancedMarkerContext)?.marker
  const blockRef = useRef<HTMLDivElement>(null)
  const [layout, setLayout] = useState<BlockLayout>(COMPACT_LAYOUT)

  const alertStatus = alertStatusOf(deviceStatus)
  const alert = alertPresentationOf(deviceStatus)
  const working = workingPresentationOf(deviceStatus)
  const AlertIcon = alert.Icon
  const WorkingIcon = working.Icon

  const attention = alertStatus === 'alarm' || alertStatus === 'error'

  const label = `${device.name} — ${t(working.labelKey)} · ${t(alert.labelKey)}`

  const place = useCallback(() => {
    const mapDiv = map?.getDiv()
    const anchor = marker?.element
    const block = blockRef.current
    if (!expanded || !mapDiv || !anchor || !block) return

    const mapRect = mapDiv.getBoundingClientRect()
    const anchorRect = anchor.getBoundingClientRect()
    const point = { x: anchorRect.left + anchorRect.width / 2 - mapRect.left, y: anchorRect.bottom - mapRect.top }

    const width = block.offsetWidth
    const height = block.offsetHeight
    const roomAbove = point.y - MAP_EDGE_INSET
    const roomBelow = mapRect.height - point.y - MAP_EDGE_INSET
    const below = height > roomAbove && roomBelow > roomAbove

    const centeredLeft = point.x - width / 2
    const left = Math.min(
      Math.max(centeredLeft, MAP_EDGE_INSET),
      Math.max(MAP_EDGE_INSET, mapRect.width - MAP_EDGE_INSET - width),
    )
    const shiftX = Math.round(left - centeredLeft)
    const tailX = Math.round(Math.min(Math.max(point.x - left, TAIL_INSET), Math.max(TAIL_INSET, width - TAIL_INSET)))

    const next: BlockLayout = {
      below,
      shiftX,
      tailX,
      maxWidth: Math.max(0, Math.round(mapRect.width - 2 * MAP_EDGE_INSET)),
    }
    setLayout((current) => (sameLayout(current, next) ? current : next))
  }, [expanded, map, marker])

  useLayoutEffect(() => {
    if (!expanded) {
      setLayout(COMPACT_LAYOUT)
      return
    }
    place()
  }, [expanded, place, layout.maxWidth])

  useEffect(() => {
    const anchor = marker?.element
    if (!expanded || !map || !anchor) return
    let frame = 0
    const schedule = () => {
      cancelAnimationFrame(frame)
      frame = requestAnimationFrame(place)
    }
    const listeners = [map.addListener('bounds_changed', schedule), map.addListener('idle', schedule)]
    /* Google przesuwa element markera zmianą `style.transform` — dopiero po niej ramka jest aktualna. */
    const mutations = new MutationObserver(schedule)
    mutations.observe(anchor, { attributes: true, attributeFilter: ['style'] })
    const resizes = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(schedule)
    resizes?.observe(map.getDiv())
    if (blockRef.current) resizes?.observe(blockRef.current)
    return () => {
      cancelAnimationFrame(frame)
      listeners.forEach((listener) => listener.remove())
      mutations.disconnect()
      resizes?.disconnect()
    }
  }, [expanded, map, marker, place])

  useEffect(() => {
    if (!expanded || !onClose) return
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [expanded, onClose])

  const resource = deviceStatus?.resource ?? device.resource ?? 0
  const resourceLabel = device.resourceLabel ?? t('device.resource')
  const inspection = device.inspections?.[0]
  const days = inspection?.validityDate ? daysUntil(inspection.validityDate) : null

  const compactHandlers = expanded
    ? {}
    : {
        onClick: () => onClick?.(device.id),
        onKeyDown: (event: React.KeyboardEvent<HTMLDivElement>) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault()
            onClick?.(device.id)
          }
        },
      }

  return (
    // Google stawia dolny środek treści `AdvancedMarker` na współrzędnych — bez własnego
    // translate grot na końcu treści ląduje w punkcie.
    <div
      ref={blockRef}
      className={cn(
        'group relative flex w-max flex-col items-stretch outline-none',
        active ? 'z-crane-pinned' : attention ? 'z-crane-raised' : 'z-crane-base',
        expanded ? 'z-crane-pinned-label' : 'cursor-pointer hover:z-crane-lifted',
      )}
      style={
        expanded
          ? { maxWidth: layout.maxWidth, transform: `translate(${layout.shiftX}px, ${layout.below ? '100%' : '0px'})` }
          : undefined
      }
      role={expanded ? 'group' : 'button'}
      tabIndex={expanded ? -1 : 0}
      aria-label={label}
      title={expanded ? undefined : label}
      {...compactHandlers}
    >
      {expanded && layout.below ? <BlockTail x={layout.tailX} up /> : null}

      <div
        className={cn(
          'flex w-full max-w-crane-popup flex-col rounded-crane-md text-crane-primary-foreground shadow-crane-soft',
          expanded ? 'bg-crane-sidebar' : 'bg-crane-sidebar/95',
          active && 'ring-1 ring-inset ring-crane-border',
          !expanded &&
            'group-hover:ring-1 group-hover:ring-inset group-hover:ring-crane-border group-focus-visible:ring-2 group-focus-visible:ring-inset group-focus-visible:ring-crane-primary',
        )}
      >
        <div className='flex min-h-10 items-center gap-2 py-1.5 pl-1.5 pr-2.5'>
          <span className='flex shrink-0 items-center gap-1'>
            <span className={`${STATE_CHIP_CLASS} ${working.dot}`}>
              <WorkingIcon className='size-3.5 text-crane-sidebar' aria-hidden />
            </span>
            <span className={`${STATE_CHIP_CLASS} ${alert.dot}`}>
              <AlertIcon className='size-3.5 text-crane-sidebar' aria-hidden />
            </span>
          </span>

          <span
            className={cn(
              'font-bold leading-4',
              expanded ? 'min-w-0 flex-1 break-words text-sm' : 'max-w-crane-tag truncate text-crane-sm',
            )}
          >
            {device.name}
          </span>

          {expanded && onClose ? (
            <Button
              type='button'
              variant='ghost'
              size='icon'
              className='-mr-1 size-10 shrink-0 text-crane-sidebar-foreground hover:bg-crane-sidebar-active/10 hover:text-crane-primary-foreground md:size-7'
              onClick={onClose}
              aria-label={t('commonActions.close')}
            >
              <X aria-hidden />
            </Button>
          ) : null}
        </div>

        {expanded ? (
          <div className='flex flex-col gap-crane-hairline px-2.5 pb-2.5 text-xs'>
            {locationLabel ? <InfoRow label={t('device.location')}>{locationLabel}</InfoRow> : null}

            <InfoRow label={t('device.serialNumber')}>{device.serialNumber}</InfoRow>
            <InfoRow label={t('device.udtNumber')}>{device.udtNumber}</InfoRow>

            <InfoRow label={resourceLabel}>{resource}%</InfoRow>

            <InfoRow
              label={t('device.udtInspection')}
              valueClassName={inspectionToneClass(days, inspection?.warningDays)}
            >
              {inspection && days !== null
                ? days < 0
                  ? t('common.inspectionExpired', { daysAgo: -days })
                  : `${formatDate(inspection.validityDate, i18n.language)} · ${t('common.daysUntilExpirationUdt', {
                      daysUntilExpirationUdt: days,
                    })}`
                : t('common.noInspection')}
            </InfoRow>

            {onDetails ? (
              <Button
                type='button'
                variant='secondary'
                size='sm'
                className='mt-2 h-11 w-full rounded-crane-sm md:h-8 md:w-auto md:self-start'
                onClick={() => onDetails(device.id)}
              >
                {t('commonActions.details')}
              </Button>
            ) : null}
          </div>
        ) : null}
      </div>

      {expanded && layout.below ? null : <BlockTail x={expanded ? layout.tailX : undefined} />}
    </div>
  )
}
